import { Component, computed, inject, signal } from '@angular/core';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { SolutionOption } from '../solution-option/solution-option';
import { SolucionAccesible } from '../../core/services/soluciones.service';

export interface DatosCambioSolucion {
  /**
   * La lista, o la promesa de la lista.
   *
   * Se acepta la promesa para que el dialogo abra apenas se hace clic, en vez
   * de que quien lo abre espere la respuesta con la pantalla quieta. El
   * cargando vive adentro, que es donde el usuario lo esta mirando.
   */
  soluciones: SolucionAccesible[] | Promise<SolucionAccesible[]>;
}

/**
 * Cambio de solución desde el menú. Se elige y se confirma en dos pasos, igual
 * que el cambio de workspace: continuar saca al usuario de esta aplicación, así
 * que no conviene que ocurra por un clic accidental en una lista.
 */
@Component({
  selector: 'app-switch-solution-modal',
  imports: [MatDialogModule, MatProgressSpinnerModule, TranslateModule, SolutionOption],
  templateUrl: './switch-solution-modal.html',
  styleUrl: './switch-solution-modal.scss',
})
export class SwitchSolutionModal {
  private dialogRef = inject(MatDialogRef<SwitchSolutionModal>);
  private datos = inject<DatosCambioSolucion>(MAT_DIALOG_DATA);

  protected readonly soluciones = signal<SolucionAccesible[]>([]);
  protected readonly cargando = signal(true);

  /** Arranca sobre la solución abierta, que es de donde parte el usuario. */
  protected readonly seleccionadaId = signal<string | null>(null);

  private readonly seleccionada = computed(
    () => this.soluciones().find(s => s.id === this.seleccionadaId()) ?? null,
  );

  constructor() {
    // `Promise.resolve` toma por igual la lista ya resuelta y la promesa, así
    // que quien abre el diálogo puede pasar cualquiera de las dos.
    Promise.resolve(this.datos.soluciones)
      .then(lista => {
        this.soluciones.set(lista ?? []);
        this.seleccionadaId.set(lista?.find(s => s.actual)?.id ?? null);
      })
      .catch(() => this.soluciones.set([]))
      .finally(() => this.cargando.set(false));
  }

  /** Ir a donde ya se está, o a algo sin dirección, no lleva a ninguna parte. */
  protected readonly puedeContinuar = computed(() => {
    const elegida = this.seleccionada();
    return !!elegida && !elegida.actual && !!elegida.url;
  });

  protected estaSeleccionada(solucion: SolucionAccesible): boolean {
    return solucion.id === this.seleccionadaId();
  }

  protected elegir(solucion: SolucionAccesible): void {
    this.seleccionadaId.set(solucion.id);
  }

  protected cancelar(): void {
    this.dialogRef.close();
  }

  protected continuar(): void {
    this.dialogRef.close(this.puedeContinuar() ? this.seleccionada() : null);
  }
}
